import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import getParamsEnv from '../../../utils/getParamsEnv';

export default function UsuariosForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [formData, setFormData] = useState({
        userName: '',
        description: '',
        email: '',
        password: '',
        roleid: '',
        userstatus: true
    });

    const [roles, setRoles] = useState([]);
    const [allPlants, setAllPlants] = useState([]); // Available plants
    const [selectedPlants, setSelectedPlants] = useState([]); // Assigned plant names

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { API_URL_BASE } = getParamsEnv();
    const API_URL = API_URL_BASE;

    useEffect(() => {
        const loadData = async () => {
            await fetchRoles();
            await fetchPlants();
            if (isEditing) {
                await fetchUser();
            }
        };
        loadData();
    }, [id]);

    const fetchRoles = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}roles`, { headers: { Authorization: `Bearer ${token}` } });
            if (response.data) setRoles(response.data);
        } catch (err) {
            console.error("Error fetching roles:", err);
        }
    };

    const fetchPlants = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}admin/plantas`, { headers: { Authorization: `Bearer ${token}` } });
            if (response.data) setAllPlants(response.data);
        } catch (err) {
            console.error("Error fetching plants:", err);
        }
    };

    const fetchUser = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_URL}user/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (response.data) {
                const user = response.data;
                setFormData({
                    userName: user.userName,
                    description: user.description || '',
                    email: user.email,
                    password: '',
                    roleid: user.roleid,
                    userstatus: user.userstatus
                });

                // Map existing accesses to selectedPlants
                if (user.AccesosPlanta && Array.isArray(user.AccesosPlanta)) {
                    setSelectedPlants(user.AccesosPlanta.map(acc => acc.pathfiltro));
                }
            }
        } catch (err) {
            setError("Error cargando usuario: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handlePlantChange = (plantName) => {
        setSelectedPlants(prev => {
            if (prev.includes(plantName)) {
                return prev.filter(p => p !== plantName);
            } else {
                return [...prev, plantName];
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            const payload = {
                ...formData,
                plantas: selectedPlants
            };
            if (!payload.password && isEditing) delete payload.password;

            if (isEditing) {
                await axios.put(`${API_URL}user/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            } else {
                await axios.post(`${API_URL}user/register`, payload, { headers: { Authorization: `Bearer ${token}` } });
            }
            navigate('/MEN025');
        } catch (err) {
            setError("Error guardando usuario: " + (err.response?.data?.message || err.message));
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen flex justify-center items-start">
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 w-full max-w-4xl">
                <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">
                    {isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}
                </h2>

                {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* User Info Section */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de Usuario *</label>
                            <input
                                type="text"
                                name="userName"
                                required
                                value={formData.userName}
                                onChange={handleChange}
                                disabled={isEditing}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50 disabled:text-gray-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
                            <input
                                type="text"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                            <input
                                type="email"
                                name="email"
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Rol *</label>
                            <select
                                name="roleid"
                                required
                                value={formData.roleid}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">Seleccione un rol</option>
                                {roles.map(role => (
                                    <option key={role.roleid} value={role.roleid}>
                                        {role.rolename}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {!isEditing && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                                <input
                                    type="password"
                                    name="password"
                                    required={!isEditing}
                                    value={formData.password}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                        )}

                        {isEditing && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña (Opcional)</label>
                                <input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Dejar en blanco para cancelar cambio"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                        )}
                    </div>

                    {/* Plant Assignment Section */}
                    <div className="border rounded-lg p-4 bg-gray-50/50">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Asignación de Plantas</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-60 overflow-y-auto">
                            {allPlants.length > 0 ? allPlants.map(plant => (
                                <label key={plant.id} className="flex items-center space-x-2 cursor-pointer p-2 hover:bg-white rounded transition-colors border border-transparent hover:border-gray-200">
                                    <input
                                        type="checkbox"
                                        checked={selectedPlants.includes(plant.planta)}
                                        onChange={() => handlePlantChange(plant.planta)}
                                        className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    />
                                    <span className="text-sm text-gray-700">{plant.planta}</span>
                                </label>
                            )) : (
                                <div className="col-span-full text-center text-gray-500 italic py-4">
                                    No hay plantas disponibles o cargando...
                                </div>
                            )}
                        </div>
                        <div className="mt-2 text-xs text-gray-500 text-right">
                            {selectedPlants.length} planta(s) seleccionada(s)
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                        <input
                            type="checkbox"
                            name="userstatus"
                            id="userstatus"
                            checked={formData.userstatus}
                            onChange={handleChange}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <label htmlFor="userstatus" className="text-sm text-gray-700 font-medium">Usuario Activo</label>
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => navigate('/MEN025')}
                            className="px-5 py-2.5 text-gray-700 font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-5 py-2.5 text-white font-medium bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm hover:shadow transition-all disabled:opacity-50"
                        >
                            {loading ? 'Guardando...' : (isEditing ? 'Actualizar Usuario' : 'Crear Usuario')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
